import { useCallback, useContext } from "react";
import { useHistory, useRouteMatch } from "react-router";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { getEntityLink, navigateEntityLink } from "../AiSearchResults";
import type { ISearchHit } from "../types";

export interface IEntityNavigation {
    /**
     * Whether this hit resolves to a route at all. Some entities need a field the
     * search payload does not always carry (a job-scoped hit with no `jobId`, a
     * submittal with no `externalId`), and some entity types have no route.
     */
    isRoutable: (hit: ISearchHit) => boolean;
    /** Navigate to the hit. A no-op when it is not routable. */
    open: (hit: ISearchHit) => void;
}

/**
 * Opening a record from search — the one implementation of it, so result rows,
 * recents and citation chips cannot route the same record differently.
 */
export function useEntityNavigation(
    builderInfo: BuilderInfo | null | undefined,
): IEntityNavigation {
    const history = useHistory();
    const match = useRouteMatch();
    const spaContext = useContext(SpaInfoContext);

    const isRoutable = useCallback(
        (hit: ISearchHit) => getEntityLink(hit, builderInfo ?? undefined) !== null,
        [builderInfo],
    );

    const open = useCallback(
        (hit: ISearchHit) => {
            const link = getEntityLink(hit, builderInfo ?? undefined);
            if (!link) {
                return;
            }
            navigateEntityLink(link, history, match.url, !!spaContext.isSpa);
        },
        [builderInfo, history, match.url, spaContext.isSpa],
    );

    return { isRoutable, open };
}
